/**
 * AI Guide Context Provider
 * Maintains AI state across the entire app journey
 * Provides contextual guidance based on user state and location
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// User emotional states the AI can detect and respond to
const EMOTIONAL_STATES = {
    CALM: 'calm',
    ANXIOUS: 'anxious',
    OVERWHELMED: 'overwhelmed',
    SHUTDOWN: 'shutdown',
    CONFUSED: 'confused',
    ACTIVATED: 'activated',
    NUMB: 'numb',
    UNKNOWN: 'unknown'
};

// App contexts the AI understands
const APP_CONTEXTS = {
    LANDING: 'landing',
    ONBOARDING: 'onboarding',
    SETTLING: 'settling',
    ASSESSMENT: 'assessment',
    DASHBOARD: 'dashboard',
    TOOL_INTRO: 'tool_intro',
    TOOL_ACTIVE: 'tool_active',
    TOOL_COMPLETE: 'tool_complete',
    EDUCATION: 'education',
    RELATIONSHIP: 'relationship',
    NAVIGATOR: 'navigator',
    AFTERCARE: 'aftercare'
};

const AIGuideContext = createContext(null);

export const useAIGuide = () => {
    const context = useContext(AIGuideContext);
    if (!context) {
        throw new Error('useAIGuide must be used within AIGuideProvider');
    }
    return context;
};

export const AIGuideProvider = ({ children }) => {
    // Session and user state
    const [sessionId] = useState(() => `journey-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const [userState, setUserState] = useState({
        emotionalState: EMOTIONAL_STATES.UNKNOWN,
        energyLevel: 'unknown', // low, medium, high
        regulationProgress: 0, // 0-100
        currentContext: APP_CONTEXTS.LANDING,
        currentTool: null,
        completedTools: [],
        assessmentData: null,
        journeyStartTime: Date.now(),
        lastInteraction: Date.now()
    });
    
    // AI conversation history for continuity
    const [conversationHistory, setConversationHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastGuidance, setLastGuidance] = useState(null);

    // Update user context when navigating
    const updateContext = useCallback((context, additionalData = {}) => {
        setUserState(prev => ({
            ...prev,
            currentContext: context,
            lastInteraction: Date.now(),
            ...additionalData
        }));
    }, []);

    // Update emotional state based on user interactions
    const updateEmotionalState = useCallback((state, energyLevel = null) => {
        setUserState(prev => ({
            ...prev,
            emotionalState: state,
            ...(energyLevel && { energyLevel }),
            lastInteraction: Date.now()
        }));
    }, []);

    // Record tool completion
    const recordToolCompletion = useCallback((toolId) => {
        setUserState(prev => ({
            ...prev,
            completedTools: [...prev.completedTools, { toolId, completedAt: Date.now() }],
            regulationProgress: Math.min(100, prev.regulationProgress + 20),
            lastInteraction: Date.now()
        }));
    }, []);

    // Get contextual AI guidance based on current state
    const getContextualGuidance = useCallback(async (userMessage = null, forceContext = null) => {
        setIsLoading(true);
        
        const context = forceContext || userState.currentContext;
        const token = localStorage.getItem('co_regulate_token');
        
        // Build context-aware message
        const contextInfo = {
            context,
            emotional_state: userState.emotionalState,
            energy_level: userState.energyLevel,
            regulation_progress: userState.regulationProgress,
            current_tool: userState.currentTool,
            completed_tools: userState.completedTools.map(t => t.toolId),
            journey_duration: Math.floor((Date.now() - userState.journeyStartTime) / 60000), // minutes
            has_completed_assessment: !!userState.assessmentData
        };

        try {
            const response = await fetch(`${API_URL}/api/guide/contextual`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    message: userMessage,
                    session_id: sessionId,
                    context_info: contextInfo
                })
            });

            if (response.ok) {
                const data = await response.json();
                
                // Add to conversation history
                if (userMessage) {
                    setConversationHistory(prev => [...prev, 
                        { role: 'user', content: userMessage, timestamp: Date.now() },
                        { role: 'assistant', content: data.response, timestamp: Date.now() }
                    ]);
                }
                
                setLastGuidance({
                    message: data.response,
                    action: data.action,
                    suggestions: data.suggestions || [],
                    tone: data.tone || 'warm',
                    timestamp: Date.now()
                });
                
                return data;
            }
        } catch (error) {
            console.error('AI Guide error:', error);
        } finally {
            setIsLoading(false);
        }
        
        return null;
    }, [sessionId, userState]);

    // Get a simple contextual prompt without user input
    const getContextPrompt = useCallback(async (context, specificNeed = null) => {
        return getContextualGuidance(specificNeed, context);
    }, [getContextualGuidance]);

    // Check if user might need support based on behavior
    const checkUserWellbeing = useCallback(() => {
        const timeSinceStart = Date.now() - userState.journeyStartTime;
        const timeSinceLastInteraction = Date.now() - userState.lastInteraction;
        
        // User has been inactive for a while during a tool
        if (userState.currentContext === APP_CONTEXTS.TOOL_ACTIVE && timeSinceLastInteraction > 120000) {
            return { needsSupport: true, reason: 'inactive_during_tool' };
        }
        
        // User has been in the app a long time without regulation progress
        if (timeSinceStart > 600000 && userState.regulationProgress < 20) {
            return { needsSupport: true, reason: 'low_progress' };
        }
        
        return { needsSupport: false };
    }, [userState]);

    const value = {
        // State
        sessionId,
        userState,
        conversationHistory,
        isLoading,
        lastGuidance,
        
        // Constants
        EMOTIONAL_STATES,
        APP_CONTEXTS,
        
        // Actions
        updateContext,
        updateEmotionalState,
        recordToolCompletion,
        getContextualGuidance,
        getContextPrompt,
        checkUserWellbeing,
        
        // Setters
        setUserState
    };

    return (
        <AIGuideContext.Provider value={value}>
            {children}
        </AIGuideContext.Provider>
    );
};

export default AIGuideContext;
